import React, { useState } from 'react';
import { Button } from '@saleshandy/design-system';
import UpgradePlanErrorModal from '../../../../../../../shared/components/upgrade-plan-error-modal';
import DropDownAtom from '../../../../../../../shared/design-system/components/atoms/dropdown';
import { Permissions } from '../../../../../../../shared/utils/access-control/enums/permissions';
import { ResourceIdentifiers } from '../../../../../../../shared/utils/access-control/enums/resource-identifiers';
import hasPermission from '../../../../../../../shared/utils/access-control/has-permission';
import hasResource from '../../../../../../../shared/utils/access-control/has-resource';
import ImportContactsModal from '../../../../../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/import-contacts-modal';
import AddProspectManuallyModal from '../../modals/add-prospect-manually-modal';
import RestrictionErrorModal from '../../modals/restriction-error-modal';
import {
  getRestrictionPlanModalBodyContent,
  getRestrictionPlanWarningEmailBody,
} from '../../../utils/helper';
import { starterPlanRestriction } from '../../../../../../../shared/utils/plan-restriction';
import { riskyProspectWarningconstants } from '../../../../../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/email-verification/risky-prospect-constant';
import { isFreePlan } from '../../../../../../../shared/utils/plans';
import planPermissionError from '../../../../../../../shared/utils/errors/plan-permission-error';
import Icon from '../../../../../../../shared/design-system/components/atoms/icon/icon';
import { redirectWithoutRefresh } from '../../../../../../../shared/utils/redirect';
import { Routes } from '../../../../../../../shared/routes';
import { AnalyticsEvents } from '../../../../../../../shared/enums/analytics';
import {
  getUserLeadFinderPlanCode,
  getUserShAccountId,
  getUserPlanCode,
} from '../../../../../../../shared/utils/user-details';
import { capturePosthogEvent } from '../../../../../../../shared/utils/initialize-third-party-integrations';

const AddContactsButton = ({
  fromModule,
  remainingProspectLimit,
  onAddProspectButtonRef = null,
  isDropDown = true,
}) => {
  const [showImportContactsModal, setShowImportContactsModal] = useState(false);
  const [
    showAddProspectManuallyModal,
    setShowAddProspectManuallyModal,
  ] = useState(false);
  const [showUpgradePlanErrorModal, setShowUpgradePlanErrorModal] = useState(
    false,
  );
  const [
    isRestrictionModalVisible,
    setIsRestrictionModalVisible,
  ] = useState<boolean>(false);

  const navigateToFindLeads = () => {
    capturePosthogEvent(AnalyticsEvents.FindNewLeadsClicked, {
      source: 'prospect',
      current_tab_url: document.location.href,
      shAccountId: getUserShAccountId(),
      leadFinderPlan: getUserLeadFinderPlanCode(),
      outreachPlan: getUserPlanCode(),
    });
    redirectWithoutRefresh(Routes.Leads_Finder_Company);
  };

  //* Show Hide Restriction Error Modal
  const showRestrictionErrorModal = () => setIsRestrictionModalVisible(true);
  const hideRestrictionErrorModal = () => setIsRestrictionModalVisible(false);

  const planUpgradeError = () => {
    const title = 'Please upgrade your plan to start adding prospects';
    const content = [
      'Your account is downgraded to a free plan. Upgrade to paid plan to add prospects and resume email sending.',
    ];

    planPermissionError.showModal(title, content, true);
  };

  const openImportContactsModal = () => {
    if (isFreePlan()) {
      planUpgradeError();
      return;
    }

    if (starterPlanRestriction()) {
      setShowUpgradePlanErrorModal(true);
      return;
    }

    if (parseInt(remainingProspectLimit, 10) === 0) {
      showRestrictionErrorModal();
      return;
    }

    hasResource(ResourceIdentifiers.CONTACTS_IMPORT) &&
      setShowImportContactsModal(true);
  };

  const options = [
    {
      icon: 'software-upload',
      onClick: openImportContactsModal,
      listKey: 'import',
      listLabel: 'Import via CSV',
    },
    hasPermission(Permissions.LEAD_FINDER_READ) && {
      icon: 'search',
      onClick: navigateToFindLeads,
      listKey: 'find-leads',
      listLabel: 'Find New Leads',
    },
  ].filter(Boolean);

  const onAddProspectsButtonClick = () => {
    if (onAddProspectButtonRef) {
      onAddProspectButtonRef.current = true;
    }
    if (isFreePlan()) {
      planUpgradeError();
      return;
    }

    if (starterPlanRestriction()) {
      setShowUpgradePlanErrorModal(true);
      return;
    }

    if (parseInt(remainingProspectLimit, 10) === 0) {
      showRestrictionErrorModal();
      return;
    }

    hasResource(ResourceIdentifiers.CONTACTS_IMPORT) &&
      setShowAddProspectManuallyModal(true);
  };

  const buttonContent = isDropDown ? (
    <div className="add-prospect-inner">
      <Button
        variant="primary"
        onClick={onAddProspectsButtonClick}
        className="add-prospect-btn"
      >
        Add Prospect
      </Button>
      <DropDownAtom heading="" options={options} />
    </div>
  ) : (
    <div className="d-flex gap-12">
      <Button variant="primary" onClick={openImportContactsModal}>
        <Icon identifier="software-upload" />
        <span className="ml-2">Import via CSV</span>
      </Button>
      {hasPermission(Permissions.LEAD_FINDER_READ) && (
        <Button variant="secondary" onClick={navigateToFindLeads}>
          <Icon identifier="search" />
          <span className="ml-2">Find New Leads</span>
        </Button>
      )}
      <Button
        variant="tertiary"
        onClick={onAddProspectsButtonClick}
        className="add-prospect-tertiary"
      >
        <Icon identifier="math-plus" />
        <span className="ml-2">Add Manually</span>
      </Button>
    </div>
  );

  return (
    <>
      {buttonContent}
      {showImportContactsModal && (
        <ImportContactsModal
          warning={riskyProspectWarningconstants.PROSPECT_DEFAULT_WARNING}
          show={showImportContactsModal}
          onClose={() => setShowImportContactsModal(false)}
          fromModule={fromModule}
        />
      )}
      {showAddProspectManuallyModal && (
        <AddProspectManuallyModal
          show={showAddProspectManuallyModal}
          onClose={() => {
            if (onAddProspectButtonRef) {
              onAddProspectButtonRef.current = false;
            }
            setShowAddProspectManuallyModal(false);
          }}
        />
      )}
      {showUpgradePlanErrorModal && (
        <UpgradePlanErrorModal
          show={showUpgradePlanErrorModal}
          onClose={() => {
            if (onAddProspectButtonRef) {
              onAddProspectButtonRef.current = false;
            }
            setShowUpgradePlanErrorModal(false);
          }}
          planName="starter"
          restrictionOn="prospect"
        />
      )}
      {isRestrictionModalVisible && (
        <RestrictionErrorModal
          show={isRestrictionModalVisible}
          onClose={hideRestrictionErrorModal}
          bodyContent={getRestrictionPlanModalBodyContent()}
          emailBody={getRestrictionPlanWarningEmailBody()}
        />
      )}
    </>
  );
};

export default AddContactsButton;
